import React, { useState, useEffect } from 'react';
import {
  VStack,
  HStack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Switch,
  Button,
  Alert,
  AlertIcon,
  Spinner,
  useToast,
  Card,
  CardBody,
  Divider,
  useColorModeValue,
  Badge,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { useApi } from '../../../contexts/ApiContext';
import {
  CordeCommonModelsDtoUserprofileUpdatePrivacySettingsRequest as UpdatePrivacySettingsRequest,
  CordeCommonModelsDtoUserprofilePrivacySettings as PrivacySettingsDto,
} from '../../../api/generated/v1/api';
import { UserProfile } from '../../../contexts/userprofile/UserProfileContext';

interface PrivacySettingsProps {
  userId: string;
  profile: UserProfile | null;
  onMarkChanged: (hasChanges: boolean) => void;
  onClearChanges: () => void;
}

interface PrivacyFormData {
  is_profile_public: boolean;
  show_email: boolean;
  show_phone: boolean;
  show_location: boolean;
  custom_settings: { [key: string]: string };
}

export const PrivacySettings: React.FC<PrivacySettingsProps> = ({
  userId,
  profile,
  onMarkChanged,
  onClearChanges,
}) => {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const api = useApi();
  const toast = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<PrivacyFormData>({
    is_profile_public: true,
    show_email: false,
    show_phone: false,
    show_location: true,
    custom_settings: {},
  });
  const [originalData, setOriginalData] = useState<PrivacyFormData>({
    is_profile_public: true,
    show_email: false,
    show_phone: false,
    show_location: true,
    custom_settings: {},
  });

  // Initialize form data from profile
  useEffect(() => {
    if (profile?.privacy_settings) {
      const settings = profile.privacy_settings as any;
      const data: PrivacyFormData = {
        is_profile_public: settings.is_profile_public ?? true,
        show_email: settings.show_email ?? false,
        show_phone: settings.show_phone ?? false,
        show_location: settings.show_location ?? true,
        custom_settings: settings.custom_settings ?? {},
      };
      setFormData(data);
      setOriginalData(data);
    } else {
      // Load privacy settings from API if not available in profile
      loadPrivacySettings();
    }
  }, [profile]);

  // Check for changes
  useEffect(() => {
    const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalData);
    onMarkChanged(hasChanges);
  }, [formData, originalData, onMarkChanged]);

  const loadPrivacySettings = async () => {
    if (!profile?.id) return;
    
    setIsLoading(true);
    try {
      const response = await api.UserprofileApi.getPrivacySettings(profile.id);
      const settings = response.data.settings;
      
      if (settings) {
        const data: PrivacyFormData = {
          is_profile_public: settings.is_profile_public ?? true,
          show_email: settings.show_email ?? false,
          show_phone: settings.show_phone ?? false,
          show_location: settings.show_location ?? true,
          custom_settings: settings.custom_settings ?? {},
        };
        setFormData(data);
        setOriginalData(data);
      }
    } catch (error: any) {
      console.error('Failed to load privacy settings:', error);
      // Use defaults if loading fails
    } finally {
      setIsLoading(false);
    }
  };

  const handleSwitchChange = (field: keyof Omit<PrivacyFormData, 'custom_settings'>, value: boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!profile?.id) {
      toast({
        title: t('settings.privacy.noProfileError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const updateRequest: UpdatePrivacySettingsRequest = {
        profile_id: profile.id,
        settings: {
          is_profile_public: formData.is_profile_public,
          show_email: formData.show_email,
          show_phone: formData.show_phone,
          show_location: formData.show_location,
          custom_settings: formData.custom_settings,
        },
      };

      await api.UserprofileApi.updatePrivacySettings(profile.id, updateRequest);
      
      // Update original data
      setOriginalData(formData);
      onClearChanges();
      
      toast({
        title: t('settings.privacy.saveSuccess'),
        description: t('settings.privacy.saveSuccessMessage'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error('Failed to save privacy settings:', error);
      toast({
        title: t('settings.privacy.saveError'),
        description: error?.response?.data?.message || error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData(originalData);
  };

  if (isLoading) {
    return (
      <VStack spacing={4}>
        <Spinner size="lg" />
        <Text>{t('common.loading')}</Text>
      </VStack>
    );
  }

  if (!profile) {
    return (
      <Alert status="warning">
        <AlertIcon />
        {t('settings.privacy.noProfile')}
      </Alert>
    );
  }

  const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalData);

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <VStack spacing={2} align="flex-start">
        <Heading size="md">{t('settings.privacy.title')}</Heading>
        <Text color="gray.600" fontSize="sm">
          {t('settings.privacy.description')}
        </Text>
      </VStack>

      {/* Profile Visibility */}
      <Card bg="bg.surface" shadow="sm" border="1px solid" borderColor="border.default">
        <CardBody>
          <VStack spacing={4} align="stretch">
            <Heading size="sm">{t('settings.privacy.profileVisibility')}</Heading>
            
            <HStack justify="space-between" align="flex-start">
              <VStack align="flex-start" spacing={1} flex={1}>
                <Text fontWeight="medium">{t('settings.privacy.publicProfile')}</Text>
                <Text fontSize="sm" color="gray.600">
                  {t('settings.privacy.publicProfileDescription')}
                </Text>
              </VStack>
              <Switch
                isChecked={formData.is_profile_public}
                onChange={(e) => handleSwitchChange('is_profile_public', e.target.checked)}
                colorScheme="blue"
                size="lg"
              />
            </HStack>

            <Divider />

            <Text fontSize="sm" color="gray.600">
              {formData.is_profile_public 
                ? t('settings.privacy.profileIsPublic')
                : t('settings.privacy.profileIsPrivate')
              }
            </Text>
          </VStack>
        </CardBody>
      </Card>

      {/* Contact Information Visibility */}
      <Card bg="bg.surface" shadow="sm" border="1px solid" borderColor="border.default">
        <CardBody>
          <VStack spacing={4} align="stretch">
            <Heading size="sm">{t('settings.privacy.contactVisibility')}</Heading>
            
            <HStack justify="space-between" align="flex-start">
              <VStack align="flex-start" spacing={1} flex={1}>
                <Text fontWeight="medium">{t('settings.privacy.showEmail')}</Text>
                <Text fontSize="sm" color="gray.600">
                  {t('settings.privacy.showEmailDescription')}
                </Text>
              </VStack>
              <Switch
                isChecked={formData.show_email}
                onChange={(e) => handleSwitchChange('show_email', e.target.checked)}
                colorScheme="blue"
                size="lg"
              />
            </HStack>

            <Divider />

            <HStack justify="space-between" align="flex-start">
              <VStack align="flex-start" spacing={1} flex={1}>
                <Text fontWeight="medium">{t('settings.privacy.showPhone')}</Text>
                <Text fontSize="sm" color="gray.600">
                  {t('settings.privacy.showPhoneDescription')}
                </Text>
              </VStack>
              <Switch
                isChecked={formData.show_phone}
                onChange={(e) => handleSwitchChange('show_phone', e.target.checked)}
                colorScheme="blue"
                size="lg"
              />
            </HStack>

            <Divider />

            <HStack justify="space-between" align="flex-start">
              <VStack align="flex-start" spacing={1} flex={1}>
                <Text fontWeight="medium">{t('settings.privacy.showLocation')}</Text>
                <Text fontSize="sm" color="gray.600">
                  {t('settings.privacy.showLocationDescription')}
                </Text>
              </VStack>
              <Switch
                isChecked={formData.show_location}
                onChange={(e) => handleSwitchChange('show_location', e.target.checked)}
                colorScheme="blue"
                size="lg"
              />
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Privacy Summary */}
      <Card bg="bg.surface" shadow="sm" border="1px solid" borderColor="border.default">
        <CardBody>
          <VStack spacing={4} align="stretch">
            <Heading size="sm">{t('settings.privacy.privacySummary')}</Heading>
            
            <HStack wrap="wrap" spacing={2}>
              <Badge
                colorScheme={formData.is_profile_public ? 'green' : 'red'}
                size="md"
                px={3}
                py={1}
                borderRadius="full"
              >
                {formData.is_profile_public 
                  ? t('settings.privacy.publicProfile')
                  : t('settings.privacy.privateProfile')
                }
              </Badge>
              
              {formData.show_email && (
                <Badge colorScheme="blue" size="md" px={3} py={1} borderRadius="full">
                  {t('settings.privacy.emailVisible')}
                </Badge>
              )}
              
              {formData.show_phone && (
                <Badge colorScheme="blue" size="md" px={3} py={1} borderRadius="full">
                  {t('settings.privacy.phoneVisible')}
                </Badge>
              )}
              
              {formData.show_location && (
                <Badge colorScheme="blue" size="md" px={3} py={1} borderRadius="full">
                  {t('settings.privacy.locationVisible')}
                </Badge>
              )}
            </HStack>

            <Text fontSize="sm" color="gray.600">
              {t('settings.privacy.privacySummaryDescription')}
            </Text>
          </VStack>
        </CardBody>
      </Card>

      {/* Save Actions */}
      {hasChanges && (
        <Card bg="bg.surface" shadow="sm" border="1px solid" borderColor="border.default">
          <CardBody>
            <HStack spacing={4} justify="flex-end">
              <Button
                variant="outline"
                onClick={handleReset}
                isDisabled={isSubmitting}
              >
                {t('common.reset')}
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleSave}
                isLoading={isSubmitting}
                loadingText={t('common.saving')}
              >
                {t('common.save')}
              </Button>
            </HStack>
          </CardBody>
        </Card>
      )}
    </VStack>
  );
}; 